import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Scale } from 'lucide-react';
import { getSettings } from '../utils/settingsCache';

const FALLBACK_SECTIONS = [
  { id: 1, title: "Acceptance of Terms", highlight: false, content: "By accessing or using www.solvibetoken.com, you confirm that you have read, understood, and agree to be bound by these Terms of Use in their entirety. If you do not agree with any part of these Terms, you must immediately stop using this website." },
  { id: 2, title: "No Financial Advice", highlight: true, content: "NOTHING on this website constitutes financial, investment, legal, or tax advice. All information published here is provided solely for informational and entertainment purposes.\n\nAlways conduct your own independent research (DYOR) and consult a qualified financial advisor before investing in any digital asset." },
  { id: 3, title: "High-Risk Asset Warning", highlight: true, content: "Cryptocurrency and digital tokens, including SOLVIBE ($SVB), are highly speculative, volatile assets. You may lose part or ALL of your invested capital. Only invest what you can afford to lose entirely." },
  { id: 4, title: "No Guarantees or Promises", highlight: false, content: "The SOLVIBE project makes no warranties regarding future value, roadmap achievement, exchange listings, or continued development. Any forward-looking statements are aspirational and do not constitute binding commitments." },
  { id: 5, title: "Jurisdictional Restrictions", highlight: true, content: "This website is NOT directed at residents or citizens of the United States of America, or any jurisdiction where the purchase or holding of digital tokens is restricted or prohibited.\n\nIt is your sole responsibility to comply with local laws." },
  { id: 6, title: "Decentralized & Community-Driven", highlight: false, content: "SOLVIBE ($SVB) is a decentralized, community-driven token. There is no formal company or centralized team controlling the token. This website is maintained voluntarily by community contributors." },
  { id: 7, title: "Limitation of Liability", highlight: true, content: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE CONTRIBUTORS AND COMMUNITY MEMBERS OF SOLVIBE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES, INCLUDING LOSS OF PROFITS OR LOSS OF DIGITAL ASSETS." },
  { id: 8, title: "Governing Law & Jurisdiction", highlight: true, content: "These Terms of Use are governed by and construed in accordance with the laws of Kosovo. Any disputes shall be subject to the exclusive jurisdiction of the competent courts of Kosovo." },
  { id: 9, title: "Contact", highlight: false, content: "For questions regarding these Terms, contact: support@solvibetoken.com" },
];

const TermsOfUseModal = ({ isOpen, onClose }) => {
  const [sections, setSections] = useState(FALLBACK_SECTIONS);
  const [lastUpdated, setLastUpdated] = useState('June 2026');

  useEffect(() => {
    if (!isOpen) return;
    getSettings().then(data => {
      if (data?.terms_sections) {
        try {
          const parsed = JSON.parse(data.terms_sections);
          if (Array.isArray(parsed) && parsed.length > 0) setSections(parsed);
        } catch (_) {}
      }
      if (data?.legal_last_updated) setLastUpdated(data.legal_last_updated);
    });
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[2147483647] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
        onClick={onClose}
        style={{ zIndex: 2147483647 }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-700 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          style={{ zIndex: 2147483647 }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-solana-primary/10 to-neon-cyan/10 border-b border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-solana-primary to-neon-cyan rounded-full w-10 h-10 flex items-center justify-center">
                  <Scale className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">Terms of Use</h2>
              </div>
              <button
                onClick={onClose}
                aria-label="Close terms of use"
                className="text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-gray-800"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[70vh] text-gray-300 leading-relaxed">
            <div className="space-y-6">
              {sections.map((section, idx) => (
                <div key={section.id ?? idx}>
                  <h3 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-solana-primary rounded-full flex-shrink-0"></span>
                    {idx + 1}. {section.title}
                  </h3>
                  {section.highlight ? (
                    <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-lg p-4">
                      <p className="text-base text-yellow-100 whitespace-pre-line font-medium">
                        {section.content}
                      </p>
                    </div>
                  ) : (
                    <p className="text-base whitespace-pre-line">
                      {section.content}
                    </p>
                  )}
                </div>
              ))}

              <div className="text-center pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-500">
                  <strong>Last updated:</strong> {lastUpdated}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-800/50 border-t border-gray-700 p-4 text-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-solana-primary to-neon-cyan text-white font-semibold rounded-lg hover:scale-105 transition-all duration-300"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TermsOfUseModal;
